import { useTranslation } from "react-i18next";
import { TableColumn } from "react-data-table-component";
import React, { ChangeEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container, Row, Col, Card, CardBody, Button } from "reactstrap";
import { useHistory } from "react-router-dom";
import { DebounceInput } from "react-debounce-input";
import moment from "moment";
import {
  ColumnHeader,
  TextColumn,
  DateColumn,
  GovernorateSelect,
  CitySelect,
  SchoolSelect,
  CourseSelect,
  GradeSelect,
  AppTable,
  TableSpinner,
} from "../../../components";
import { BreadCrumb, BreadCrumbSelect } from "../../../components/Common";
import {
  State,
  TransactionHistoryDto,
  TransactionHistoryPurchaseDto,
} from "../../../interfaces";
import { Paginated, PaginateQuery } from "../../../models";
import {
  resetCitySelectList,
  resetSchoolSelectList,
  resetGradeSelectListByCourseLocal,
  getCourseAndGradesSelectList,
  getTransactionHistory,
} from "../../../store/actions";
import Section from "../../Dashboards/AdminDashboard/Section";
import { PaymentStatusCopiedEnum, PaymentThroughEnum } from "../../../enums";

type TProps = {};
export const TransactionHistoryPage: React.FC<TProps> = () => {
  //#region hooks

  const history = useHistory();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  //#endregion

  //#region redux state

  const { loading, response } = useSelector(
    (state: any): State<Paginated<TransactionHistoryDto>> =>
      state.transaction.transactionHistory
  );

  //#endregion

  //#region internal state

  const initState = new PaginateQuery(10, "transaction_date:DESC", {
    ["from"]: moment(new Date()).format("yyyy-MM-DD"),
    ["to"]: moment(new Date()).format("yyyy-MM-DD"),
    ["status"]: "Paid",
  });
  const [paginateQuery, setPaginateQuery] = useState<PaginateQuery>(initState);
  const [filterGovernorateId, setFilterGovernorateId] = useState<string>("");
  const [filterCityId, setFilterCityId] = useState<string>("");
  const [filterSchoolId, setFilterSchoolId] = useState<string>("");
  const [filterCourseId, setFilterCourseId] = useState<string>("");
  const [filterGradeId, setFilterGradeId] = useState<string>("");
  const [filterGenderId, setFilterGenderId] = useState<string>("");
  const [filterStatusId, setFilterStatusId] = useState<string>("Paid");
  const [filterMethodId, setFilterMethodId] = useState<string>("");
  const [dateValue, setDateValue] = useState([
    moment(new Date()).format("yyyy-MM-DD"),
    moment(new Date()).format("yyyy-MM-DD"),
  ]);
  //#endregion

  document.title = `${t("documentTitle", { pageName: "transactionHistory" })}`;

  const columns: TableColumn<TransactionHistoryDto>[] = [
    {
      name: <ColumnHeader title="student" />,
      cell: (row) => (
        <React.Fragment>
          <div>
            <div className="my-2">
              <strong>{t("name")}: </strong>
              <strong className="text-capitalize">
                {row.user?.name[i18n.language === "ar" ? "ar" : "en"] || "n/a"}
              </strong>
            </div>
            <div className="my-2">
              <strong>{t("gender")}: </strong>
              <span>{row.user?.gender ? row.user?.gender : "n/a"}</span>
            </div>
            <div className="my-2">
              <strong>{t("course")}: </strong>
              <span>
                {row.purchases &&
                row.purchases.length > 0 &&
                row.purchases[0].course &&
                row.purchases[0].course?.name
                  ? row.purchases[0].course?.name[
                      i18n.language === "ar" ? "ar" : "en"
                    ]
                  : "n/a"}
              </span>
            </div>
            <div className="my-2">
              <strong>{t("grade")}: </strong>
              <span>
                {row.purchases &&
                row.purchases.length > 0 &&
                row.purchases[0].grade &&
                row.purchases[0].grade?.name
                  ? row.purchases[0].grade?.name[
                      i18n.language === "ar" ? "ar" : "en"
                    ]
                  : "n/a"}
              </span>
            </div>
            <div className="my-2">
              <strong>{t("phoneNumber")}: </strong>
              <a
                style={{ cursor: "pointer" }}
                href={`tel:+${row.user?.phone?.countryCode}${row.user?.phone?.phoneNumber}`}
              >
                <u>{`+${row.user?.phone?.countryCode}${row.user?.phone?.phoneNumber}`}</u>
              </a>
            </div>
            <div className="my-2">
              <strong>{t("subjectsCount")}: </strong>
              <span>{row.purchases.length}</span>
            </div>
          </div>
        </React.Fragment>
      ),
      minWidth: "300px",
    },
    {
      name: <ColumnHeader title="transactionDate" />,
      cell: (row) => <DateColumn date={row.createdAt} />,
      sortable: true,
      width: "200px",
    },
    {
      name: <ColumnHeader title="address" />,
      cell: (row) => (
        <React.Fragment>
          <div>
            <div className="my-2">
              <strong>{t("governorate")}: </strong>
              <span>
                {row.user?.addressGovernorate?.name
                  ? row.user?.addressGovernorate?.name[
                      i18n.language === "ar" ? "ar" : "en"
                    ]
                  : "n/a"}
              </span>
            </div>
            <div className="my-2">
              <strong>{t("city")}: </strong>
              <span>
                {row.user?.addressCity?.name
                  ? row.user?.addressCity?.name[
                      i18n.language === "ar" ? "ar" : "en"
                    ]
                  : "n/a"}
              </span>
            </div>
            <div className="my-2">
              <strong>{t("school")}: </strong>
              <span>
                {row.user?.school?.name
                  ? row.user?.school?.name[i18n.language === "ar" ? "ar" : "en"]
                  : "n/a"}
              </span>
            </div>
          </div>
        </React.Fragment>
      ),
      sortable: false,
      width: "250px",
    },
    {
      name: <ColumnHeader title="status" />,
      cell: (row) => <TextColumn text={row.status || "-"} />,
      sortable: false,
    },
    {
      name: <ColumnHeader title="paymentMethod" />,
      cell: (row) => <TextColumn text={row.paymentThrough || "-"} />,
      sortable: false,
      width: "200px",
    },
    {
      name: <ColumnHeader title="invoiceId" />,
      cell: (row) => (
        <TextColumn
          text={
            row.invoiceId != 0 &&
            row.invoiceId !== undefined &&
            row.invoiceId !== null
              ? row.invoiceId + ""
              : "-"
          }
        />
      ),
      sortable: false,
    },
    {
      name: <ColumnHeader title="amount" />,
      cell: (row) => (
        <TextColumn
          text={
            row.amount != 0 && row.amount !== undefined && row.amount !== null
              ? row.amount + ""
              : "-"
          }
        />
      ),
      sortable: false,
    },
    {
      name: <ColumnHeader title="currency" />,
      cell: (row) => <TextColumn text={row.currency || "-"} />,
      sortable: false,
    },
    {
      name: <ColumnHeader title="promoCode" />,
      cell: (row) => <TextColumn text={row.promoCode || "-"} />,
      sortable: false,
    },
  ];

  const getSortByColumnName = (name: string) => {
    let sortBy;
    switch (name) {
      case "transaction_date":
        sortBy = 2;
        break;
      default:
        sortBy = 2;
        break;
    }
    return sortBy;
  };

  const [defaultSortFieldId, setDefaultSortFieldId] = useState(
    getSortByColumnName("transaction_date:DESC")
  );
  const [defaultSortAsc, setDefaultSortAsc] = useState(false);
  const [defaultPage, setDefaultPage] = useState(1);

  //#region effects

  useEffect(() => {
    const queryParams = new URLSearchParams(history.location.search);
    let filters: any = {};
    if (queryParams.has("filter[governorate]")) {
      filters = {
        ...filters,
        ["governorate"]: queryParams.get("filter[governorate]") || "",
      };
      setFilterGovernorateId(filters["governorate"] as string);
    }
    if (queryParams.has("filter[city]")) {
      filters = {
        ...filters,
        ["city"]: queryParams.get("filter[city]") || "",
      };
      setFilterCityId(filters["city"] as string);
    }
    if (queryParams.has("filter[school]")) {
      filters = {
        ...filters,
        ["school"]: queryParams.get("filter[school]") || "",
      };
      setFilterSchoolId(filters["school"] as string);
    }
    if (queryParams.has("filter[course]")) {
      filters = {
        ...filters,
        ["course"]: queryParams.get("filter[course]") || "",
      };
      setFilterCourseId(filters["course"] as string);
    }
    if (queryParams.has("filter[grade]")) {
      filters = {
        ...filters,
        ["grade"]: queryParams.get("filter[grade]") || "",
      };
      setFilterGradeId(filters["grade"] as string);
    }
    if (queryParams.has("filter[gender]")) {
      filters = {
        ...filters,
        ["gender"]: queryParams.get("filter[gender]") || "",
      };
      setFilterGenderId(filters["gender"] as string);
    }
    if (queryParams.has("filter[status]")) {
      filters = {
        ...filters,
        ["status"]: queryParams.get("filter[status]") || "",
      };
      setFilterStatusId(filters["status"] as string);
    }
    if (queryParams.has("filter[method]")) {
      filters = {
        ...filters,
        ["method"]: queryParams.get("filter[method]") || "",
      };
      setFilterMethodId(filters["method"] as string);
    }
    if (queryParams.has("filter[from]")) {
      filters = {
        ...filters,
        ["from"]: queryParams.get("filter[from]") || "",
      };
    }
    if (queryParams.has("filter[to]")) {
      filters = {
        ...filters,
        ["to"]: queryParams.get("filter[to]") || "",
      };
    }
    let query: PaginateQuery = {
      limit: queryParams.has("limit")
        ? Number(queryParams.get("limit"))
        : paginateQuery.limit,
      page: queryParams.has("page")
        ? Number(queryParams.get("page"))
        : paginateQuery.page,
      sortBy: queryParams.has("sortBy")
        ? (queryParams.get("sortBy") as string)
        : paginateQuery.sortBy,
      search: queryParams.has("search")
        ? (queryParams.get("search") as string)
        : paginateQuery.search,
      filter: Object.keys(filters).length > 0 ? filters : paginateQuery.filter,
    };
    if (
      queryParams.get("sortBy") &&
      queryParams.get("sortBy")?.split(":").length &&
      queryParams.get("sortBy")?.split(":").length == 2
    ) {
      setDefaultSortAsc(
        (queryParams.get("sortBy")?.split(":")[1] as string) === "ASC"
      );
      setDefaultSortFieldId(
        getSortByColumnName(queryParams.get("sortBy")?.split(":")[0] as string)
      );
    } else {
      setDefaultSortAsc(false);
      setDefaultSortFieldId(2);
    }
    setDefaultPage(query.page);
    setPaginateQuery(query);
  }, []);

  useEffect(() => {
    if (paginateQuery !== initState) {
      const { limit, page, search, sortBy, filter } = paginateQuery;
      let query;
      if (page) query = `?page=${page}`;
      if (limit) query += `&limit=${limit}`;
      if (search && search !== "") {
        history.replace({
          search: `&search=${search}`,
        });
      }
      if (sortBy) query += `&sortBy=${sortBy}`;
      if (search) query += `&search=${search}`;
      if (
        filter &&
        Object.keys(filter).length &&
        Object.keys(filter).length > 0
      ) {
        let filters: string = "";
        Object.keys(filter).map((key: string) => {
          if (filter[key]) filters += `&filter[${key}]=${filter[key]}`;
        });
        query += filters;
      }
      history.replace({ search: query });
      dispatch(getTransactionHistory(paginateQuery, history));
    }
  }, [paginateQuery]);

  useEffect(() => {
    dispatch(resetCitySelectList());
    dispatch(resetSchoolSelectList());
    dispatch(resetGradeSelectListByCourseLocal());
    dispatch(getCourseAndGradesSelectList());
  }, []);

  //#endregion

  //#region handlers

  const onSearch = (searchText: string) => {
    setPaginateQuery({ ...paginateQuery, search: searchText });
  };

  const getSortByColumnId = (columnId: number, sortDirection: string) => {
    let sortBy;
    switch (columnId) {
      case 2:
        sortBy = "transaction_date";
        break;
      default:
        sortBy = "transaction_date";
        break;
    }
    return `${sortBy}:${sortDirection.toUpperCase()}`;
  };

  const onChangePage = (page: number) => {
    setPaginateQuery({ ...paginateQuery, page: page });
  };

  const onChangeRowsPerPage = (
    currentRowsPerPage: number,
    currentPage: number
  ) => {
    setPaginateQuery({
      ...paginateQuery,
      limit: currentRowsPerPage,
      page: currentPage,
    });
  };

  const onSort = (
    column: TableColumn<TransactionHistoryDto>,
    sortDirection: string
  ) => {
    const sortBy = getSortByColumnId(column.id as number, sortDirection);
    setPaginateQuery({
      ...paginateQuery,
      sortBy: sortBy as string,
    });
  };

  const onChangeGovernorateFilter = (value: string) => {
    setFilterSchoolId("");
    dispatch(resetSchoolSelectList());
    setFilterCityId("");
    dispatch(resetCitySelectList());
    setFilterGovernorateId(value);
    setPaginateQuery({
      ...paginateQuery,
      filter: {
        ...paginateQuery.filter,
        ["governorate"]: value,
        ["city"]: "",
        ["school"]: "",
      },
    });
  };

  const onChangeCityFilter = (value: string) => {
    setFilterSchoolId("");
    dispatch(resetSchoolSelectList());
    setFilterCityId(value);
    setPaginateQuery({
      ...paginateQuery,
      filter: {
        ...paginateQuery.filter,
        ["city"]: value,
        ["school"]: "",
      },
    });
  };

  const onChangeSchoolFilter = (value: string) => {
    setFilterSchoolId(value);
    setPaginateQuery({
      ...paginateQuery,
      filter: {
        ...paginateQuery.filter,
        ["school"]: value,
      },
    });
  };

  const onChangeCourseFilter = (value: string) => {
    setFilterGradeId("");
    dispatch(resetGradeSelectListByCourseLocal());
    setFilterCourseId(value);
    setPaginateQuery({
      ...paginateQuery,
      filter: {
        ...paginateQuery.filter,
        ["course"]: value,
        ["grade"]: "",
      },
    });
  };

  const onChangeGradeFilter = (value: string) => {
    setFilterGradeId(value);
    setPaginateQuery({
      ...paginateQuery,
      filter: {
        ...paginateQuery.filter,
        ["grade"]: value,
      },
    });
  };

  const onChangeGenderFilter = (value: string) => {
    setFilterGenderId(value);
    setPaginateQuery({
      ...paginateQuery,
      filter: {
        ...paginateQuery.filter,
        ["gender"]: value,
      },
    });
  };

  const onChangeStatusFilter = (value: string) => {
    setFilterStatusId(value);
    setPaginateQuery({
      ...paginateQuery,
      filter: {
        ...paginateQuery.filter,
        ["status"]: value,
      },
    });
  };

  const onChangeMethodFilter = (value: string) => {
    setFilterMethodId(value);
    setPaginateQuery({
      ...paginateQuery,
      filter: {
        ...paginateQuery.filter,
        ["method"]: value,
      },
    });
  };

  const onClearFilters = () => {
    setFilterGovernorateId("");
    setFilterCityId("");
    dispatch(resetCitySelectList());
    setFilterSchoolId("");
    dispatch(resetSchoolSelectList());
    setFilterCourseId("");
    setFilterGradeId("");
    dispatch(resetGradeSelectListByCourseLocal());
    setFilterGenderId("");
    setFilterStatusId("");
    setFilterMethodId("");
    setPaginateQuery({
      ...paginateQuery,
      filter: {
        ...paginateQuery.filter,
        ["governorate"]: "",
        ["city"]: "",
        ["school"]: "",
        ["course"]: "",
        ["grade"]: "",
        ["gender"]: "",
        ["status"]: "",
        ["method"]: "",
      },
    });
  };

  const onChangeDate = (dates: any) => {
    if (dates && dates.length === 2) {
      setDateValue(dates);
      setPaginateQuery({
        ...paginateQuery,
        filter: {
          ...paginateQuery.filter,
          ["from"]: moment(new Date(dates[0])).format("yyyy-MM-DD"),
          ["to"]: moment(new Date(dates[1])).format("yyyy-MM-DD"),
        },
      });
    } else {
      setPaginateQuery({
        ...paginateQuery,
        filter: {
          ...paginateQuery.filter,
          ["from"]: "",
          ["to"]: "",
        },
      });
    }
  };
  //#endregion

  const ExpandedComponent = ({ data }: { data: TransactionHistoryDto }) => (
    <Row className="py-5">
      {data.purchases.map(
        (purchase: TransactionHistoryPurchaseDto, index: number) => (
          <Col className="py-2 col-xl-1 col-lg-2 col-md-3 col-sm-12">
            <Row>
              <Col className="d-flex justify-content-center">
                <div className="team-profile-img">
                  <div className="avatar-lg img-thumbnail rounded-circle flex-shrink-0">
                    {purchase.subject.imagePath != null ? (
                      <img
                        src={purchase.subject.imagePath}
                        alt=""
                        className="img-fluid d-block rounded-circle"
                      />
                    ) : (
                      <div className="avatar-title text-uppercase border rounded-circle bg-light text-primary">
                        {purchase.subject?.name?.en.charAt(0) +
                          purchase.subject?.name?.en
                            .split(" ")
                            .slice(-1)
                            .toString()
                            .charAt(0)}
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
            <Row>
              <Col>
                <div className="text-center">
                  <div className="team-content pt-2">
                    <h3 className="fs-16 mb-1">
                      {i18n.language === "ar"
                        ? purchase.subject.name?.ar
                        : purchase.subject.name?.en}
                    </h3>
                  </div>
                  <div className="team-content pt-1">
                    <h4 className="fs-14 mb-1">
                      {i18n.language === "ar"
                        ? purchase.course?.name?.ar
                        : purchase.course?.name?.en}
                    </h4>
                  </div>
                  <div className="team-content pt-1">
                    <h5 className="fs-12 mb-1">
                      {i18n.language === "ar"
                        ? purchase.grade?.name?.ar
                        : purchase.grade?.name?.en}
                    </h5>
                  </div>
                </div>
              </Col>
            </Row>
          </Col>
        )
      )}
    </Row>
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("transactionHistory")}
            pageTitle={t("payment")}
            component={
              <BreadCrumbSelect
                options={[
                  {
                    title: "transactionHistory",
                    url: "/payment/transaction-history",
                    selected: true,
                  },
                  {
                    title: "transactionHistory2",
                    url: "/payment/transaction-history-v2",
                    selected: false,
                  },
                ]}
              />
            }
          />
          <Row>
            <Col>
              <div className="h-100">
                <Section
                  value={dateValue}
                  onChange={(value: any) => onChangeDate(value)}
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col xxl={12}>
              <Card>
                <CardBody>
                  <Row className="g-2 mb-3">
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <DebounceInput
                          className="form-control"
                          placeholder={`${t("searchPlaceholder")}...`}
                          minLength={2}
                          debounceTimeout={300}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                          ) => onSearch(event.target.value)}
                          value={paginateQuery.search}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <CourseSelect
                          value={filterCourseId}
                          placeholder={t("filterByCourse")}
                          type="select"
                          id="courseId"
                          name="courseId"
                          className="form-select"
                          aria-label={t("filterByCourse")}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            onChangeCourseFilter(event.target.value)
                          }
                        ></CourseSelect>
                      </div>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <GradeSelect
                          courseId={filterCourseId}
                          value={filterGradeId}
                          placeholder={t("filterByGrade")}
                          type="select"
                          id="gradeId"
                          name="gradeId"
                          className="form-select"
                          aria-label={t("filterByGrade")}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            onChangeGradeFilter(event.target.value)
                          }
                        ></GradeSelect>
                      </div>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <GovernorateSelect
                          value={filterGovernorateId}
                          placeholder={t("filterByAddressGovernorate")}
                          type="select"
                          id="governorateId"
                          name="governorateId"
                          className="form-select"
                          aria-label={t("filterByAddressGovernorate")}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            onChangeGovernorateFilter(event.target.value)
                          }
                        ></GovernorateSelect>
                      </div>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <CitySelect
                          value={filterCityId}
                          placeholder={t("filterByAddressCity")}
                          type="select"
                          id="cityId"
                          name="cityId"
                          className="form-select"
                          aria-label={t("filterByAddressCity")}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            onChangeCityFilter(event.target.value)
                          }
                          governorateId={filterGovernorateId}
                        ></CitySelect>
                      </div>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <SchoolSelect
                          value={filterSchoolId}
                          placeholder={t("filterBySchool")}
                          type="select"
                          id="schoolId"
                          name="schoolId"
                          className="form-select"
                          aria-label={t("filterBySchool")}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            onChangeSchoolFilter(event.target.value)
                          }
                          governorateId={filterGovernorateId}
                          cityId={filterCityId}
                        ></SchoolSelect>
                      </div>
                    </Col>

                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <select
                          value={filterGenderId}
                          className="form-select form-select-md"
                          onChange={(event: any) =>
                            onChangeGenderFilter(event.target.value)
                          }
                        >
                          <option value="">{t("filterByGender")}</option>
                          <option value="Female">{t("female")}</option>
                          <option value="Male">{t("male")}</option>
                        </select>
                      </div>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <select
                          value={filterStatusId}
                          className="form-select form-select-md"
                          onChange={(event: any) =>
                            onChangeStatusFilter(event.target.value)
                          }
                        >
                          <option value="">{t("filterByStatus")}</option>
                          {(
                            Object.keys(PaymentStatusCopiedEnum) as Array<
                              keyof typeof PaymentStatusCopiedEnum
                            >
                          ).map((key) => (
                            <option value={key}>
                              {PaymentStatusCopiedEnum[key]}
                            </option>
                          ))}
                        </select>
                      </div>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <select
                          value={filterMethodId}
                          className="form-select form-select-md"
                          onChange={(event: any) =>
                            onChangeMethodFilter(event.target.value)
                          }
                        >
                          <option value="">{t("filterByPaymentMethod")}</option>
                          {(
                            Object.keys(PaymentThroughEnum) as Array<
                              keyof typeof PaymentThroughEnum
                            >
                          ).map((key) => (
                            <option value={key}>{key}</option>
                          ))}
                        </select>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col className="col-sm-auto ms-auto">
                      <div className="list-grid-nav hstack gap-1">
                        <Button
                          color="success"
                          onClick={() => onClearFilters()}
                        >
                          {t("clearFilters")}
                        </Button>
                        <Button
                          color="success"
                          onClick={() => setPaginateQuery({ ...paginateQuery })}
                        >
                          {t("refresh")}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={12}>
                      <AppTable
                        theme={"dark"}
                        columns={columns}
                        data={response?.data || []}
                        progressPending={loading}
                        progressComponent={<TableSpinner />}
                        pagination
                        paginationServer
                        sortServer
                        onSort={onSort}
                        onChangeRowsPerPage={onChangeRowsPerPage}
                        onChangePage={onChangePage}
                        defaultSortFieldId={defaultSortFieldId}
                        paginationDefaultPage={defaultPage}
                        defaultSortAsc={defaultSortAsc}
                        persistTableHead
                        paginationTotalRows={response?.meta?.totalItems}
                        paginationComponentOptions={{
                          rowsPerPageText: t("rowsPerPage"),
                          rangeSeparatorText: t("of"),
                        }}
                        fixedHeader={true}
                        fixedHeaderScrollHeight="60vh"
                        expandableRows
                        expandableRowsComponent={ExpandedComponent}
                      />
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};
